import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { errorMessage } from '../lib/api'
import { useConfirm } from '../pages/roster/ConfirmDialog'
import { levelName } from '../lib/levelName'
import {
  useAppointAdmin,
  useCreateOrganization,
  usePlatformOperator,
  usePlatformOrganizationPeople,
  usePlatformOrganizations,
  useRemoveAdmin,
  useSetAdminPassword,
  useSetEntraTenant,
  useSetOrganizationDeactivated,
  type NewOrganization,
} from '../lib/platform'
import { offeredReplyTo } from '../lib/replyTo'
import { signOutAndLand } from '../lib/teams'
import type { PlatformOrganization, PlatformPerson } from '../lib/types'
import {
  btnGhost,
  btnPrimary,
  btnReject,
  cardClass,
  Checkbox,
  emptyStateClass,
  inputClass,
  labelClass,
  ModalBackdrop,
  mutedTextClass,
  dialogInsetPad,
} from './ui'

const ADMIN_TIER = 'admin'
const ENTRA_TENANT_PATTERN = '[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}'

function Refusal({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-reject dark:border-red-900 dark:bg-red-950/40">
      {errorMessage(error)}
    </p>
  )
}

export default function PlatformConsole() {
  const operator = usePlatformOperator()
  const organizations = usePlatformOrganizations()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Derived, not stored: an organization that has left the list must not stay selected with a stale name
  // beside a people panel that 404s.
  const selected = useMemo(
    () => organizations.data?.find((org) => org.id === selectedId) ?? null,
    [organizations.data, selectedId],
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="flex flex-wrap items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="mr-auto">
          <h1 className="font-semibold text-slate-800 dark:text-slate-100">Platform console</h1>
          <p className={mutedTextClass}>
            CenTrig control plane — no tenant identity, no tenant data
          </p>
        </div>
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {operator.data ? operator.data.display_name || operator.data.email : 'Operator'}
        </span>
        <button
          type="button"
          onClick={() => {
            void signOutAndLand()
          }}
          className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Sign out
        </button>
      </header>

      <main className="grid gap-4 p-6 lg:grid-cols-[22rem_1fr]">
        <section className={`${cardClass} space-y-3`}>
          <div className="flex items-center gap-2">
            <h2 className="mr-auto text-sm font-semibold text-slate-800 dark:text-slate-100">
              Organizations
            </h2>
            <button type="button" className={btnPrimary} onClick={() => setCreating(true)}>
              New
            </button>
          </div>

          <Refusal error={organizations.error} />

          {organizations.isLoading && <p className={mutedTextClass}>Loading…</p>}
          {organizations.data?.length === 0 && (
            <p className={emptyStateClass}>No organizations yet.</p>
          )}

          <ul className="space-y-1">
            {organizations.data?.map((org) => (
              <li key={org.id}>
                <OrganizationRow
                  org={org}
                  selected={org.id === selectedId}
                  onSelect={() => setSelectedId(org.id)}
                />
              </li>
            ))}
          </ul>
        </section>

        <section className={`${cardClass} space-y-3`}>
          {selected ? (
            <OrganizationPeople key={selected.id} org={selected} />
          ) : (
            <p className={emptyStateClass}>Pick an organization to see its people.</p>
          )}
        </section>
      </main>

      {creating && <NewOrganizationDialog onClose={() => setCreating(false)} />}
    </div>
  )
}

function OrganizationRow({
  org,
  selected,
  onSelect,
}: {
  org: PlatformOrganization
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
        selected
          ? 'bg-accent/10 text-slate-900 dark:text-slate-100'
          : 'hover:bg-slate-50 dark:hover:bg-slate-700/60'
      }`}
    >
      <span className="block font-medium text-slate-800 dark:text-slate-100">{org.name_en}</span>
      <span className={mutedTextClass}>
        {org.slug} · {org.person_count} people ·{' '}
        {/*
           The number this list exists to make visible: a tenant on zero administrators cannot repair
           itself from inside.
        */}
        {org.admin_count === 0 ? (
          <span className="font-medium text-reject">no administrator</span>
        ) : (
          `${org.admin_count} admin${org.admin_count === 1 ? '' : 's'}`
        )}
      </span>
    </button>
  )
}

function OrganizationPeople({ org }: { org: PlatformOrganization }) {
  const { t } = useTranslation()
  const people = usePlatformOrganizationPeople(org.id)
  const appoint = useAppointAdmin()
  const remove = useRemoveAdmin()
  const { confirmDelete, dialog: deleteDialog } = useConfirm()
  const [search, setSearch] = useState('')
  const [passwordFor, setPasswordFor] = useState<PlatformPerson | null>(null)

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return people.data ?? []
    return (people.data ?? []).filter(
      (person) =>
        person.email.toLowerCase().includes(needle) ||
        person.display_name.toLowerCase().includes(needle),
    )
  }, [people.data, search])

  const refusalFor = (personId: string): unknown => {
    if (appoint.error && appoint.variables?.personId === personId) return appoint.error
    if (remove.error && remove.variables?.personId === personId) return remove.error
    return null
  }

  return (
    <>
      {deleteDialog}
      {passwordFor && (
        <AdminPasswordDialog
          key={passwordFor.id}
          org={org}
          person={passwordFor}
          onClose={() => setPasswordFor(null)}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-sm font-semibold text-slate-800 dark:text-slate-100">
          {org.name_en}
        </h2>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search people"
          className={inputClass}
        />
      </div>

      <EntraTenantForm key={org.entra_tenant_id ?? 'none'} org={org} />
      {org.is_customer && <CompanyAccess org={org} confirm={confirmDelete} />}

      <Refusal error={people.error} />
      {people.isLoading && <p className={mutedTextClass}>Loading…</p>}
      {people.data?.length === 0 && (
        <p className={emptyStateClass}>This organization has nobody in it.</p>
      )}
      {/*
         Distinct from the line above: a tenant with people in it, none of which match the search, would
         otherwise render a blank panel that reads as a failed load.
      */}
      {people.data && people.data.length > 0 && matches.length === 0 && (
        <p className={emptyStateClass}>Nobody matches “{search}”.</p>
      )}

      <ul className="divide-y divide-border-subtle">
        {matches.map((person) => (
          <li key={person.id} className="space-y-2 py-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="mr-auto">
                <span className="block text-sm text-slate-800 dark:text-slate-100">
                  {person.display_name || person.email}
                </span>
                <span className={mutedTextClass}>
                  {person.email}
                  {person.tier
                    ? ` · ${levelName(t, { slug: person.tier, label: person.tier_label })}`
                    : ' · no access level'}
                  {person.account_active === false ? ' · deactivated' : ''}
                </span>
              </div>
              {org.is_customer && person.tier === ADMIN_TIER && (
                <button type="button" className={btnGhost} onClick={() => setPasswordFor(person)}>
                  Set password
                </button>
              )}
              <AdminButton
                person={person}
                busy={appoint.isPending || remove.isPending}
                onAppoint={() => appoint.mutate({ orgId: org.id, personId: person.id })}
                onRemove={async () => {
                  const ok = await confirmDelete(`Remove ${person.display_name || person.email} as administrator?`, {
                    okLabel: 'Remove administrator',
                  })
                  if (ok) remove.mutate({ orgId: org.id, personId: person.id })
                }}
              />
            </div>
            <Refusal error={refusalFor(person.id)} />
          </li>
        ))}
      </ul>
    </>
  )
}

function CompanyAccess({
  org,
  confirm,
}: {
  org: PlatformOrganization
  confirm: ReturnType<typeof useConfirm>['confirmDelete']
}) {
  const setDeactivated = useSetOrganizationDeactivated()
  const isOff = org.deactivated_at !== null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <p className={`mr-auto ${mutedTextClass}`}>
          {isOff
            ? `Turned off on ${org.deactivated_at?.slice(0, 10)}: nobody here can sign in, and their mail and reminders are stopped.`
            : 'This company is on. Turning it off ends every sign-in at once and stops its mail and reminders. Nothing is deleted.'}
        </p>
        {isOff ? (
          <button
            type="button"
            className={btnGhost}
            disabled={setDeactivated.isPending}
            onClick={() => setDeactivated.mutate({ orgId: org.id, deactivated: false })}
          >
            Turn back on
          </button>
        ) : (
          <button
            type="button"
            className={btnReject}
            disabled={setDeactivated.isPending}
            onClick={async () => {
              const ok = await confirm(`Turn off ${org.name_en}? Everyone there is signed out at once.`, {
                okLabel: 'Turn off',
              })
              if (ok) setDeactivated.mutate({ orgId: org.id, deactivated: true })
            }}
          >
            Turn off this company
          </button>
        )}
      </div>
      <Refusal error={setDeactivated.error} />
    </div>
  )
}

function EntraTenantForm({ org }: { org: PlatformOrganization }) {
  const setTenant = useSetEntraTenant()
  const saved = org.entra_tenant_id ?? ''
  const [value, setValue] = useState(saved)
  const trimmed = value.trim()

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setTenant.mutate({ orgId: org.id, tenantId: trimmed || null })
      }}
      className="space-y-2"
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="block w-full text-sm sm:w-96">
          <span className={labelClass}>Microsoft tenant ID</span>
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            pattern={ENTRA_TENANT_PATTERN}
            title="A tenant ID looks like 00000000-0000-0000-0000-000000000000"
            className={`${inputClass} w-full`}
          />
        </label>
        <button
          type="submit"
          className={btnGhost}
          disabled={setTenant.isPending || trimmed === saved}
          title={trimmed === saved ? 'Nothing has changed' : undefined}
        >
          {setTenant.isPending ? 'Saving…' : 'Save tenant'}
        </button>
      </div>
      {!saved && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          No tenant set: nobody here can sign in with Microsoft.
        </p>
      )}
      <Refusal error={setTenant.error} />
    </form>
  )
}

function AdminPasswordDialog({
  org,
  person,
  onClose,
}: {
  org: PlatformOrganization
  person: PlatformPerson
  onClose: () => void
}) {
  const setPassword = useSetAdminPassword()
  const [generated, setGenerated] = useState<string | null>(null)
  const [email, setEmail] = useState(person.email)
  const name = person.display_name || person.email

  if (generated) {
    return (
      <ModalBackdrop onClose={onClose} aria-label={`Temporary password for ${name}`}>
        <div className={`${dialogInsetPad[6]} w-full max-w-md space-y-3 rounded-xl border border-border-subtle bg-surface p-6`}>
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">Temporary password for {name}</h2>
          <p className={mutedTextClass}>
            This is the only time it is shown. Tell them yourself: it is not emailed and nobody can read it again.
            They sign in with it and a code sent to {email.trim()}, then choose their own password.
          </p>
          <p className="break-all rounded-lg border border-border-subtle p-3 font-mono text-sm text-slate-800 dark:text-slate-100">
            {generated}
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={() => void navigator.clipboard.writeText(generated)}>
              Copy
            </button>
            <button type="button" className={btnPrimary} onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </ModalBackdrop>
    )
  }

  return (
    <ModalBackdrop onClose={onClose} aria-label={`Set a password for ${name}`}>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          setPassword.mutate(
            { orgId: org.id, personId: person.id, email: email.trim() },
            { onSuccess: (answer) => setGenerated(answer.temporary_password) },
          )
        }}
        className="w-full max-w-md space-y-3 rounded-xl border border-border-subtle bg-surface p-6"
      >
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">Set a password for {name}</h2>
        <p className={mutedTextClass}>
          The server generates a temporary password and shows it to you once. They sign in with it and a code sent
          to the address below, then choose their own password. Anywhere they are signed in now ends. If the address
          changes or they already had a password, their current address gets a notice.
        </p>
        <Refusal error={setPassword.error} />
        <label className="block text-sm">
          <span className={labelClass}>Email for the sign-in code</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={`${inputClass} w-full`}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose} disabled={setPassword.isPending}>
            Cancel
          </button>
          <button type="submit" className={btnPrimary} disabled={setPassword.isPending}>
            {setPassword.isPending ? 'Generating…' : 'Generate password'}
          </button>
        </div>
      </form>
    </ModalBackdrop>
  )
}

function AdminButton({
  person,
  busy,
  onAppoint,
  onRemove,
}: {
  person: PlatformPerson
  busy: boolean
  onAppoint: () => void
  onRemove: () => void
}) {
  if (person.tier === ADMIN_TIER) {
    return (
      <button type="button" className={btnReject} disabled={busy} onClick={onRemove}>
        Remove administrator
      </button>
    )
  }
  return (
    <button type="button" className={btnGhost} disabled={busy} onClick={onAppoint}>
      Make administrator
    </button>
  )
}

function NewOrganizationDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateOrganization()
  const [draft, setDraft] = useState<NewOrganization>({
    slug: '',
    name_en: '',
    name_mn: '',
    name_ru: '',
    registration_no: '',
    email: '',
    mail_reply_to: '',
    admin_email: '',
    admin_name: '',
    different_customer: false,
  })
  const [replyToEdited, setReplyToEdited] = useState(false)
  const replyTo = offeredReplyTo(draft.email ?? '', draft.mail_reply_to, replyToEdited)

  const set =
    (field: keyof NewOrganization) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((current) => ({ ...current, [field]: event.target.value }))

  return (
    <ModalBackdrop onClose={onClose} aria-label="New organization">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          create.mutate(
            { ...draft, mail_reply_to: replyTo, entra_tenant_id: draft.entra_tenant_id?.trim() || null },
            { onSuccess: onClose },
          )
        }}
        className="w-full max-w-lg space-y-3 rounded-xl border border-border-subtle bg-surface p-6"
      >
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">New organization</h2>

        <label className="block text-sm">
          <span className={labelClass}>Slug</span>
          <input
            required
            pattern="[a-z0-9][a-z0-9-]*"
            minLength={2}
            maxLength={50}
            value={draft.slug}
            onChange={set('slug')}
            className={`${inputClass} w-full`}
          />
          <span className={mutedTextClass}>
            Lower-case letters, digits and dashes. Set once and never changed.
          </span>
        </label>

        {(['name_en', 'name_mn', 'name_ru'] as const).map((field) => (
          <label key={field} className="block text-sm">
            <span className={labelClass}>Name ({field.slice(-2).toUpperCase()})</span>
            <input
              required
              value={draft[field]}
              onChange={set(field)}
              className={`${inputClass} w-full`}
            />
          </label>
        ))}

        <label className="block text-sm">
          <span className={labelClass}>Registration number</span>
          <input
            required
            pattern=".*\S.*"
            value={draft.registration_no}
            onChange={set('registration_no')}
            maxLength={50}
            className={`${inputClass} w-full`}
          />
          <span className={mutedTextClass}>
            Printed on every document the tenant issues; without it nothing can be issued.
          </span>
        </label>

        <label className="block text-sm">
          <span className={labelClass}>Organization&rsquo;s e-mail (optional)</span>
          <input
            type="email"
            value={draft.email ?? ''}
            onChange={set('email')}
            className={`${inputClass} w-full`}
          />
        </label>

        <label className="block text-sm">
          <span className={labelClass}>Reply-to address</span>
          <input
            required
            type="email"
            value={replyTo}
            onChange={(event) => {
              setReplyToEdited(true)
              setDraft((current) => ({ ...current, mail_reply_to: event.target.value }))
            }}
            maxLength={320}
            className={`${inputClass} w-full`}
          />
          <span className={mutedTextClass}>
            Their mail goes out from our address; replies come here. Offered from the
            organization&rsquo;s e-mail.
          </span>
        </label>

        <label className="block text-sm">
          <span className={labelClass}>First administrator&rsquo;s e-mail</span>
          <input
            required
            type="email"
            value={draft.admin_email}
            onChange={set('admin_email')}
            className={`${inputClass} w-full`}
          />
          <span className={mutedTextClass}>
            The tenant and its first administrator are one act. Without one, nobody inside can
            grant anybody a level.
          </span>
        </label>

        <label className="block text-sm">
          <span className={labelClass}>Administrator&rsquo;s name (optional)</span>
          <input
            value={draft.admin_name ?? ''}
            onChange={set('admin_name')}
            className={`${inputClass} w-full`}
          />
        </label>

        <label className="block text-sm">
          <span className={labelClass}>Microsoft tenant ID (optional)</span>
          <input
            value={draft.entra_tenant_id ?? ''}
            onChange={set('entra_tenant_id')}
            pattern={ENTRA_TENANT_PATTERN}
            title="A tenant ID looks like 00000000-0000-0000-0000-000000000000"
            className={`${inputClass} w-full`}
          />
        </label>

        <Checkbox
          required
          checked={draft.different_customer}
          onChange={(checked) => setDraft((current) => ({ ...current, different_customer: checked }))}
          label="This is a different customer, not one of our own companies. Our own trading names are issuer identities in our organization; a second organization for one of them leaves its roster and payroll empty."
        />

        <Refusal error={create.error} />

        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className={btnPrimary}
            disabled={create.isPending || !draft.different_customer}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </ModalBackdrop>
  )
}
